import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pager } from "../components/Pager";
import { Card, Chips, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type ReminderDay } from "../lib/api";

type State = "" | "due" | "sent" | "cancelled";

const REASONS: Record<string, string> = {
  tcpa_outside_window: "outside 11am–8pm ET",
  appointment_cancelled_via_webhook: "appointment cancelled",
  rescheduled_via_webhook: "appointment moved",
  "suppressed: opted_out": "opted out",
};
const reason = (r?: string | null) => (r ? REASONS[r] ?? r.replace(/_/g, " ") : "");

function etDay(offset = 0) {
  const d = new Date(Date.now() + offset * 86_400_000);
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" }); // YYYY-MM-DD
}

/** Appointment reminders: what goes out, when, and what was cancelled and why.
 *
 *  These used to run with no screen at all - a reminder that did not go was
 *  found out when someone missed a call. */
export function Reminders() {
  const [day, setDay] = useState(etDay());
  const [state, setState] = useState<State>("");
  const [calendar, setCalendar] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData] = useState<ReminderDay | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setPage(1), [day, state, calendar, q, perPage]);
  useEffect(() => {
    const t = setTimeout(() => {
      api
        .reminders({ day, state, calendar, q, page, per_page: perPage })
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((e) => setError(e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [day, state, calendar, q, page, perPage]);

  const s = data?.summary;
  return (
    <>
      <ErrorBanner error={error} />
      <div className="row w">
        <h2 style={{ fontSize: 19 }}>Appointment reminders</h2>
        <div className="sp" />
        <Chips<string>
          value={day}
          onChange={setDay}
          options={[
            { key: etDay(-1), label: "Yesterday" },
            { key: etDay(), label: "Today" },
            { key: etDay(1), label: "Tomorrow" },
          ]}
        />
        <input className="f" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      </div>

      {s && (
        <div className="tiles" style={{ margin: "10px 0 14px" }}>
          <div className="tile ok">
            <span className="lab">Sent</span>
            <span className="v">{fmt.n(s.sent)}</span>
          </div>
          <div className="tile">
            <span className="lab">Still to go</span>
            <span className="v">{fmt.n(s.due)}</span>
          </div>
          <div className={`tile ${s.overdue > 0 ? "ba" : ""}`}>
            <span className="lab">Overdue</span>
            <span className="v">{fmt.n(s.overdue)}</span>
            <span className="d">due 15+ minutes ago, not sent</span>
          </div>
          <div className="tile">
            <span className="lab">Cancelled</span>
            <span className="v">{fmt.n(s.cancelled)}</span>
            <span className="d">
              {Object.entries(s.cancelled_because)
                .map(([k, v]) => `${v} ${reason(k)}`)
                .join(" · ") || "none"}
            </span>
          </div>
          <div className="tile">
            <span className="lab">Appointments</span>
            <span className="v">{fmt.n(s.appointments)}</span>
            <span className="d">with a reminder this day</span>
          </div>
        </div>
      )}

      <Card
        title="The schedule"
        footer={
          <span className="dim">
            Times are Eastern. A reminder is cancelled rather than sent late: over an hour late,
            outside 11am–8pm ET Mon–Sat, after the call has started, or when the appointment is
            moved or cancelled (the moved one gets a new set).
          </span>
        }
      >
        <div className="row w" style={{ marginBottom: 12 }}>
          <input
            className="f"
            style={{ width: 220 }}
            placeholder="Search name or phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Chips<State>
            value={state}
            onChange={setState}
            options={[
              { key: "", label: "All" },
              { key: "due", label: "Still to go" },
              { key: "sent", label: "Sent" },
              { key: "cancelled", label: "Cancelled" },
            ]}
          />
          <select className="f" value={calendar} onChange={(e) => setCalendar(e.target.value)}>
            <option value="">All calendars</option>
            {data?.calendars.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {!data ? (
          <div className="empty">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="empty">No reminders match.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Goes out</th>
                  <th>Reminder</th>
                  <th>Person</th>
                  <th>Appointment</th>
                  <th>Calendar</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id}>
                    <td className="mono" style={{ whiteSpace: "nowrap" }}>
                      {fmt.et(r.send_at)}
                    </td>
                    <td>{r.kind_label}</td>
                    <td>
                      <Link to={`/contacts/${r.contact_id}`}>{r.name ?? r.phone}</Link>
                      <div className="dim mono" style={{ fontSize: 11 }}>
                        {r.phone}
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{fmt.et(r.appointment_at)}</td>
                    <td className="dim">{r.calendar ?? "—"}</td>
                    <td>
                      <Pill
                        tone={
                          r.state === "sent" ? "ok" : r.state === "overdue" ? "ba" : r.state === "due" ? "wa" : "nu"
                        }
                      >
                        {r.state === "due" ? "to go" : r.state}
                      </Pill>
                      {r.why_cancelled && (
                        <div className="dim" style={{ fontSize: 11 }}>
                          {reason(r.why_cancelled)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <Pager
            page={data.page}
            pages={data.pages}
            total={data.total}
            perPage={perPage}
            onPage={setPage}
            onPerPage={setPerPage}
          />
        )}
      </Card>
    </>
  );
}
